import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, ArrowLeft } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { useSeo } from '@/lib/seo';
import ShowcaseHero from '@/components/showcase/ShowcaseHero';
import ShowcaseGallery from '@/components/showcase/ShowcaseGallery';
import ShowcaseVideo from '@/components/showcase/ShowcaseVideo';
import ShowcaseRatings from '@/components/showcase/ShowcaseRatings';
import ShowcaseSpecs from '@/components/showcase/ShowcaseSpecs';
import ShowcaseExperience from '@/components/showcase/ShowcaseExperience';
import ShowcaseGames from '@/components/showcase/ShowcaseGames';
import ShowcaseReviews from '@/components/showcase/ShowcaseReviews';
import ShowcaseCTA from '@/components/showcase/ShowcaseCTA';
import ShowcaseRelatedRigs from '@/components/showcase/ShowcaseRelatedRigs';

export default function SimulatorDetail() {
  const { slug } = useParams();

  const { data: rig, isLoading } = useQuery({
    queryKey: ['showcase-rig', slug],
    queryFn: async () => {
      const rows = await base44.entities.ShowcaseRig.filter({ slug });
      if (!rows || rows.length === 0) {
        // fallback by id
        try { return await base44.entities.ShowcaseRig.get(slug); } catch { return null; }
      }
      return rows[0];
    },
  });

  useSeo({
    title: rig ? `${rig.name} — Simulator Showcase` : 'Simulator Showcase',
    description: rig?.short_description || 'Explore our professional-grade sim racing simulators.',
    image: rig?.hero_image_url,
    type: 'article',
    jsonLd: rig ? {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: rig.name,
      description: rig.short_description || rig.full_description,
      image: rig.hero_image_url ? [rig.hero_image_url] : undefined,
      category: rig.category,
      brand: { '@type': 'Brand', name: 'Motion Reality' },
      aggregateRating: rig.overall_rating ? {
        '@type': 'AggregateRating',
        ratingValue: rig.overall_rating,
        bestRating: 10,
        ratingCount: 1,
      } : undefined,
      offers: {
        '@type': 'Offer',
        priceCurrency: 'GBP',
        availability: 'https://schema.org/InStock',
        url: rig.booking_url || 'https://www.motionreality.co.uk/bookings',
      },
    } : undefined,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!rig) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex flex-col items-center justify-center gap-4">
        <p className="text-white/40 text-sm">Simulator not found.</p>
        <Link to="/simulators" className="text-primary text-xs font-bold tracking-widest hover:underline">
          ← BACK TO SIMULATORS
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-[#0d0d0f]">
      <ShowcaseHero rig={rig} />
      <ShowcaseGallery images={rig.gallery_images} />
      <ShowcaseVideo rig={rig} />
      <ShowcaseRatings rig={rig} />
      <ShowcaseSpecs rig={rig} />
      <ShowcaseExperience rig={rig} />
      <ShowcaseGames rig={rig} />
      <ShowcaseReviews rig={rig} />
      <ShowcaseCTA rig={rig} />
      <ShowcaseRelatedRigs rig={rig} />
    </div>
  );
}